/**
 * Admin Panel - Credentials Encryption API
 *
 * Operational surface for the credential-encryption estate, across the four
 * tables that hold secrets at rest:
 *
 * - **ApiKeyConnection** (`credentials` Json) — report which rows are plaintext /
 *   encrypted / double-encrypted, encrypt the plaintext ones, re-encrypt
 *   everything under the active key, and repair rows encrypted twice.
 * - **ToolCredentials** (`apikey`, `accessToken`, `refreshToken`) — report how
 *   many secrets are still in the clear, and encrypt them.
 * - **UserCredential** (`key`) — the same, over a customer's own OpenAI or
 *   Lambda AI provider key.
 * - **OAuthConnection** (`accessToken`, `refreshToken`) — the same, over live
 *   customer grants. Its status route NAMES the columns still in the clear,
 *   because a cron refreshes these rows continuously and an operator has to be
 *   able to compare the list before a run against the outcomes after it.
 *
 * The `ToolCredentials` half exists because its backfill previously shipped only
 * as a shell script, and Porter deployments have no CLI access — so the rows it
 * targeted could never actually be migrated. Anything an operator must run in
 * production is reachable from the admin panel for that reason.
 *
 * Base path: /api/admin-panel/credentials-encryption
 *
 * Auth: PBAC — `admin:credentials-encryption` read (status) / write (mutations).
 * Each per-table backfill is separately grantable, under
 * `admin:credentials-encryption:tool-credentials`,
 * `admin:credentials-encryption:user-credentials` and
 * `admin:credentials-encryption:oauth-connections`. The backend pins every one
 * of those against its route in
 * `apps/backend/src/admin/credentials-encryption/__tests__/credentials-encryption-route-surface.spec.ts`.
 *
 * ## No credential material crosses this boundary
 *
 * These responses describe the ENVELOPE, never its contents. `activeKeyId`,
 * `availableKeys`, `keyId` and `byKeyId` are key *identifiers* (`listKeyIds()`
 * returns the map's keys, not the key material); every per-connection entry
 * carries id / service / organization / status only. That is why the routes
 * carry no `sensitivity` marker — see `no-credential-bearing-tool.spec.ts`.
 * If a field ever starts carrying decrypted credentials, mark the route
 * `sensitivity: ROUTE_SENSITIVITY.SECRET` instead of contracting it.
 */

import { z } from "zod";

import { DbEnum } from "../../../generated/prisma-enums";
import { ApiSuccessSchema } from "../../common/response.schema";

// ============================================================
// Shared primitives
// ============================================================

/** Encryption state of a single stored credential row. */
export const CREDENTIAL_ENCRYPTION_STATUS_VALUES = [
  "plaintext",
  "encrypted",
  "double-encrypted"
] as const;

export const CredentialEncryptionStatusSchema = z.enum(CREDENTIAL_ENCRYPTION_STATUS_VALUES);

/**
 * A per-row failure from one of the batch operations.
 *
 * `error` is the caught exception's message — never the credential itself.
 */
export const CredentialsEncryptionErrorSchema = z.object({
  id: z.string(),
  service: z.string(),
  error: z.string()
});

/** One ApiKeyConnection, described by envelope only. */
export const CredentialsEncryptionConnectionSchema = z.object({
  id: z.string(),
  service: z.enum(DbEnum.API_KEY_SERVICE_VALUES),
  organizationId: z.string(),
  organizationName: z.string().nullable(),
  status: CredentialEncryptionStatusSchema,
  /** Envelope key IDENTIFIER (absent on plaintext rows). Not key material. */
  keyId: z.string().optional(),
  createdAt: z.string()
});

// ============================================================
// Response payloads
// ============================================================

/**
 * The second table this panel governs: the three `ToolCredentials` columns
 * `schema.prisma` annotates `/// encrypted` — `apikey`, `accessToken`,
 * `refreshToken`.
 *
 * Counted per COLUMN, not per row, because one row carries up to three
 * independent secrets that migrate independently. `columns.plaintext === 0` is
 * the completion signal the tolerant read path's retirement waits on.
 *
 * The counter is named `columns` for what it counts, and that name is
 * load-bearing rather than cosmetic: `no-credential-bearing-tool.spec.ts` walks
 * every admin response for credential-SHAPED field names, and `secrets` matches
 * its `SUSPECT_FIELD` pattern. Its escape hatch, `REVIEWED_NON_SECRET_FIELDS`,
 * is keyed on the ROUTE — so excusing the name would exempt this entire status
 * response from that walk, on the one admin route whose subject matter actually
 * is credentials. Naming the field accurately keeps the ratchet armed here.
 *
 * No `connections`-style per-row listing: unlike ApiKeyConnection, nothing here
 * is actionable per row — the backfill is all-or-nothing and the only question
 * the panel must answer is "how much is left".
 */
export const ToolCredentialsEncryptionStatusSchema = z.object({
  rows: z.number(),
  rowsWithPlaintext: z.number(),
  columns: z.object({
    total: z.number(),
    plaintext: z.number(),
    encrypted: z.number(),
    empty: z.number(),
    /** Envelope key IDENTIFIERS, never key material. */
    byKeyId: z.record(z.string(), z.number())
  })
});

/**
 * The third table this panel governs: `UserCredential.key`, a customer's own
 * OpenAI / Lambda AI provider key.
 *
 * Counted per ROW rather than per column — unlike `ToolCredentials`, the model
 * carries exactly one secret, so a row is either migrated or it is not and there
 * is no partial state to express. `plaintext === 0` is the completion signal the
 * tolerant read path's retirement waits on, and it means the same thing as the
 * absence of the `user-credential-plaintext-at-rest` log marker.
 *
 * No per-row listing: nothing here is actionable per row — the backfill is
 * all-or-nothing and the only question the panel must answer is "how much is
 * left". Naming a row would also mean naming an organization beside a credential
 * count, which this response has no reason to carry.
 */
export const UserCredentialsEncryptionStatusSchema = z.object({
  rows: z.number(),
  plaintext: z.number(),
  encrypted: z.number(),
  empty: z.number(),
  /** Envelope key IDENTIFIERS, never key material. */
  byKeyId: z.record(z.string(), z.number())
});

/**
 * The fourth table this panel governs: `OAuthConnection.{accessToken,
 * refreshToken}` — live customer grants for Gmail, Outlook, Slack and the rest.
 *
 * It gets its OWN pair of routes rather than joining the combined status
 * response, because unlike the other three it names the rows it would touch.
 * Those grants are refreshed continuously by a cron, so an operator about to run
 * the backfill needs the list up front and needs to compare it against what the
 * run reports afterwards — a bare count cannot support that.
 *
 * Counted per COLUMN: one connection carries two tokens that migrate
 * independently, and a row-level count could not express "access token migrated,
 * refresh token raced".
 *
 * ## Four states, not two
 *
 * `encrypted` means the column parses as an envelope, OPENS, yields a string
 * token, and that token is not itself an envelope. The other outcomes are
 * reported separately and never folded into it:
 *
 * - `doubleEncrypted` — sealed twice. The read path unwraps one layer and hands
 *   envelope JSON to the provider as a bearer token, and no error branch fires.
 * - `unreadable` — an envelope this process cannot open. The sealing key is
 *   missing, rotated away, or the ciphertext is corrupt.
 *
 * Counting either as encrypted would report a finished migration over
 * connections that are already broken.
 *
 * No token material crosses this boundary: entries carry id, column NAME,
 * organization and service; `byKeyId` holds key IDENTIFIERS only.
 */
export const OAuthConnectionsEncryptionStatusSchema = z.object({
  rows: z.number(),
  rowsWithPlaintext: z.number(),
  /** Non-empty token columns; the four state counts below sum to this. */
  columnsTotal: z.number(),
  plaintext: z.number(),
  encrypted: z.number(),
  doubleEncrypted: z.number(),
  unreadable: z.number(),
  /** NULL or empty columns — nothing to encrypt, counted so totals reconcile. */
  empty: z.number(),
  /** Envelope key IDENTIFIERS, never key material. */
  byKeyId: z.record(z.string(), z.number()),
  /** Every column still in the clear, named. Identity only. */
  plaintextColumns: z.array(
    z.object({
      id: z.string(),
      /** `"accessToken"` or `"refreshToken"` — the column NAME, not its value. */
      column: z.string(),
      organizationId: z.string(),
      service: z.string()
    })
  )
});

/**
 * The `OAuthConnection` backfill result.
 *
 * Like its two siblings it carries no `success` flag: **a run with any failure
 * does not return 2xx.**
 *
 * `columnsSkippedRaced` is specific to this table and is NOT a failure. Tokens
 * here are refreshed by a cron while the backfill runs, so every write is a
 * compare-and-swap on the exact value that was read. A lost swap means a live
 * writer got there first — and every live writer encrypts, so the column is
 * already correct and holds a newer token than the one this run saw. Re-running
 * is how an operator drives that number to zero.
 */
export const OAuthConnectionsMigrateResultSchema = z.object({
  /** Envelope key IDENTIFIER every column was stamped with. Not key material. */
  activeKeyId: z.string(),
  stats: z.object({
    rows: z.number(),
    columnsMigrated: z.number(),
    columnsAlreadyEncrypted: z.number(),
    columnsSkippedRaced: z.number(),
    columnsEmpty: z.number()
  }),
  /** Per-column outcome, every column. Identity and verdict only. */
  columns: z.array(
    z.object({
      id: z.string(),
      column: z.string(),
      outcome: z.enum(["migrated", "already-encrypted", "skipped-raced", "empty", "failed"])
    })
  )
});

export const CredentialsEncryptionStatusSchema = z.object({
  configured: z.boolean(),
  activeKeyId: z.string().nullable(),
  availableKeys: z.array(z.string()),
  stats: z.object({
    total: z.number(),
    plaintext: z.number(),
    encrypted: z.number(),
    doubleEncrypted: z.number(),
    byKeyId: z.record(z.string(), z.number())
  }),
  connections: z.array(CredentialsEncryptionConnectionSchema),
  toolCredentials: ToolCredentialsEncryptionStatusSchema,
  userCredentials: UserCredentialsEncryptionStatusSchema
});

export const CredentialsEncryptionMigrateResultSchema = z.object({
  success: z.boolean(),
  stats: z.object({
    total: z.number(),
    encrypted: z.number(),
    alreadyEncrypted: z.number(),
    failed: z.number()
  }),
  errors: z.array(CredentialsEncryptionErrorSchema)
});

export const CredentialsEncryptionRotateResultSchema = z.object({
  success: z.boolean(),
  stats: z.object({
    total: z.number(),
    rotated: z.number(),
    encrypted: z.number(),
    alreadyCorrect: z.number(),
    failed: z.number()
  }),
  errors: z.array(CredentialsEncryptionErrorSchema)
});

/**
 * The `ToolCredentials` backfill result.
 *
 * Carries no `success` flag and no `errors[]`, and that is the contract rather
 * than an omission: **a run with any failure does not return 2xx.** The shell
 * script this replaces exits non-zero on a partial run
 * (`encrypt-existing-tool-credentials.ts`), and a partial backfill reported as
 * HTTP success would retire the tolerant read path over rows that still need it.
 * So a 2xx body is always a complete run, and a failed one arrives as an error
 * whose message names the failing `id.column` pairs.
 */
export const ToolCredentialsMigrateResultSchema = z.object({
  /** Envelope key IDENTIFIER every column was stamped with. Not key material. */
  activeKeyId: z.string(),
  stats: z.object({
    rows: z.number(),
    columnsEncrypted: z.number(),
    columnsAlreadyEncrypted: z.number(),
    columnsEmpty: z.number()
  })
});

/**
 * The `UserCredential` backfill result.
 *
 * Carries no `success` flag and no `errors[]`, for the same reason as its
 * `ToolCredentials` sibling: **a run with any failure does not return 2xx.** A
 * partial backfill reported as HTTP success would retire the tolerant read path
 * over rows that still need it, so a 2xx body is always a complete run and a
 * failed one arrives as an error naming the failing `organizationId/provider`
 * pairs — never the keys themselves.
 */
export const UserCredentialsMigrateResultSchema = z.object({
  /** Envelope key IDENTIFIER every row was stamped with. Not key material. */
  activeKeyId: z.string(),
  stats: z.object({
    rows: z.number(),
    encrypted: z.number(),
    alreadyEncrypted: z.number(),
    empty: z.number()
  })
});

export const CredentialsEncryptionFixDoubleResultSchema = z.object({
  success: z.boolean(),
  stats: z.object({
    total: z.number(),
    fixed: z.number(),
    notDoubleEncrypted: z.number(),
    failed: z.number()
  }),
  errors: z.array(CredentialsEncryptionErrorSchema)
});

// ============================================================
// ZAdminCredentialsEncryption - Endpoint Definitions
// ============================================================

export const ZAdminCredentialsEncryption = {
  /**
   * Encryption status across every ApiKeyConnection.
   * GET /api/admin-panel/credentials-encryption/status
   */
  GetStatus: {
    method: "GET" as const,
    path: "/api/admin-panel/credentials-encryption/status",
    Response: ApiSuccessSchema(CredentialsEncryptionStatusSchema)
  },

  /**
   * Encrypt every plaintext credential with the active key.
   * POST /api/admin-panel/credentials-encryption/migrate
   */
  Migrate: {
    method: "POST" as const,
    path: "/api/admin-panel/credentials-encryption/migrate",
    Response: ApiSuccessSchema(CredentialsEncryptionMigrateResultSchema)
  },

  /**
   * Re-encrypt every credential under the active key.
   * POST /api/admin-panel/credentials-encryption/rotate
   */
  Rotate: {
    method: "POST" as const,
    path: "/api/admin-panel/credentials-encryption/rotate",
    Response: ApiSuccessSchema(CredentialsEncryptionRotateResultSchema)
  },

  /**
   * Encrypt every legacy plaintext `ToolCredentials` secret.
   * POST /api/admin-panel/credentials-encryption/tool-credentials/migrate
   *
   * Idempotent — an already-encrypted column is skipped, so re-running is how an
   * operator confirms the previous run finished. Never rotates: converting
   * plaintext and re-keying encrypted rows are separate operations with
   * different blast radii.
   */
  MigrateToolCredentials: {
    method: "POST" as const,
    path: "/api/admin-panel/credentials-encryption/tool-credentials/migrate",
    Response: ApiSuccessSchema(ToolCredentialsMigrateResultSchema)
  },

  /**
   * Encrypt every legacy plaintext `UserCredential.key`.
   * POST /api/admin-panel/credentials-encryption/user-credentials/migrate
   *
   * Idempotent — an already-encrypted row is skipped, so re-running is how an
   * operator confirms the previous run finished. Never rotates: converting
   * plaintext and re-keying encrypted rows are separate operations with
   * different blast radii.
   */
  MigrateUserCredentials: {
    method: "POST" as const,
    path: "/api/admin-panel/credentials-encryption/user-credentials/migrate",
    Response: ApiSuccessSchema(UserCredentialsMigrateResultSchema)
  },

  /**
   * How many `OAuthConnection` tokens are still in the clear, and which ones.
   * GET /api/admin-panel/credentials-encryption/oauth-connections/status
   *
   * Reads only, and needs no encryption key: an environment without one reports
   * every envelope as `unreadable` rather than failing, and `plaintext` — the
   * number the migration waits on — is derived from the stored SHAPE and is
   * correct either way.
   */
  GetOAuthConnectionsStatus: {
    method: "GET" as const,
    path: "/api/admin-panel/credentials-encryption/oauth-connections/status",
    Response: ApiSuccessSchema(OAuthConnectionsEncryptionStatusSchema)
  },

  /**
   * Encrypt every legacy plaintext `OAuthConnection` access and refresh token.
   * POST /api/admin-panel/credentials-encryption/oauth-connections/migrate
   *
   * Idempotent — an already-encrypted column is skipped, so re-running is how an
   * operator confirms the previous run finished and how a raced column gets a
   * second chance. Never rotates and never repairs a double-encrypted row: both
   * are separate operations with different blast radii.
   */
  MigrateOAuthConnections: {
    method: "POST" as const,
    path: "/api/admin-panel/credentials-encryption/oauth-connections/migrate",
    Response: ApiSuccessSchema(OAuthConnectionsMigrateResultSchema)
  },

  /**
   * Repair rows that were encrypted twice.
   * POST /api/admin-panel/credentials-encryption/fix-double-encryption
   */
  FixDoubleEncryption: {
    method: "POST" as const,
    path: "/api/admin-panel/credentials-encryption/fix-double-encryption",
    Response: ApiSuccessSchema(CredentialsEncryptionFixDoubleResultSchema)
  }
} as const;

// ============================================================
// Convenience Type Exports
// ============================================================

export type CredentialEncryptionStatus = z.infer<typeof CredentialEncryptionStatusSchema>;
export type CredentialsEncryptionConnection = z.infer<typeof CredentialsEncryptionConnectionSchema>;
export type CredentialsEncryptionStatus = z.infer<typeof CredentialsEncryptionStatusSchema>;
export type ToolCredentialsEncryptionStatus = z.infer<typeof ToolCredentialsEncryptionStatusSchema>;
export type ToolCredentialsMigrateResult = z.infer<typeof ToolCredentialsMigrateResultSchema>;
export type UserCredentialsEncryptionStatus = z.infer<typeof UserCredentialsEncryptionStatusSchema>;
export type UserCredentialsMigrateResult = z.infer<typeof UserCredentialsMigrateResultSchema>;
export type OAuthConnectionsEncryptionStatus = z.infer<
  typeof OAuthConnectionsEncryptionStatusSchema
>;
export type OAuthConnectionsMigrateResult = z.infer<typeof OAuthConnectionsMigrateResultSchema>;
export type CredentialsEncryptionMigrateResult = z.infer<
  typeof CredentialsEncryptionMigrateResultSchema
>;
export type CredentialsEncryptionRotateResult = z.infer<
  typeof CredentialsEncryptionRotateResultSchema
>;
export type CredentialsEncryptionFixDoubleResult = z.infer<
  typeof CredentialsEncryptionFixDoubleResultSchema
>;
